import { useState } from "react";
import { Link, Redirect } from "wouter";
import { Bookmark, FileText, Calendar, Tag, Briefcase, Wrench, Trash2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useSEO } from "@/hooks/use-seo";
import {
  useGetSavedItems,
  useDeleteSavedItem,
  useGetNewsById,
  useGetEventById,
  useGetDealById,
  useGetBusinessById,
  useGetJobById,
} from "@workspace/api-client-react";

type ItemType = "news" | "event" | "deal" | "business" | "job";
type TabKey = "all" | ItemType;

const TABS: { key: TabKey; label: string; icon: React.ReactNode }[] = [
  { key: "all",      label: "All",        icon: <Bookmark size={14} /> },
  { key: "news",     label: "News",       icon: <FileText size={14} /> },
  { key: "event",    label: "Events",     icon: <Calendar size={14} /> },
  { key: "deal",     label: "Deals",      icon: <Tag size={14} /> },
  { key: "business", label: "Businesses", icon: <Briefcase size={14} /> },
  { key: "job",      label: "Jobs",       icon: <Wrench size={14} /> },
];

const TYPE_COLORS: Record<ItemType, { bg: string; text: string }> = {
  news:     { bg: "bg-blue-50 dark:bg-blue-900/20",    text: "text-blue-700" },
  event:    { bg: "bg-violet-50",  text: "text-violet-700" },
  deal:     { bg: "bg-amber-50 dark:bg-amber-900/20",   text: "text-amber-700" },
  business: { bg: "bg-emerald-50", text: "text-emerald-700" },
  job:      { bg: "bg-red-50",     text: "text-red-700" },
};

const TYPE_LABELS: Record<ItemType, string> = {
  news: "NEWS", event: "EVENT", deal: "DEAL", business: "BUSINESS", job: "JOB",
};

function NewsCard({ itemId, savedId, onUnsave }: { itemId: number; savedId: number; onUnsave: (id: number) => void }) {
  const { data, isLoading } = useGetNewsById(itemId);
  if (isLoading) return <CardSkeleton />;
  if (!data) return null;
  return (
    <SavedCard
      itemType="news"
      href={`/news/${itemId}`}
      title={data.title}
      sub={data.summary ?? undefined}
      meta={data.category}
      savedId={savedId}
      onUnsave={onUnsave}
    />
  );
}

function EventCard({ itemId, savedId, onUnsave }: { itemId: number; savedId: number; onUnsave: (id: number) => void }) {
  const { data, isLoading } = useGetEventById(String(itemId));
  if (isLoading) return <CardSkeleton />;
  if (!data) return null;
  return (
    <SavedCard
      itemType="event"
      href={`/events/${itemId}`}
      title={data.name}
      meta={[data.eventDate, data.venue].filter(Boolean).join(" · ")}
      savedId={savedId}
      onUnsave={onUnsave}
    />
  );
}

function DealCard({ itemId, savedId, onUnsave }: { itemId: number; savedId: number; onUnsave: (id: number) => void }) {
  const { data, isLoading } = useGetDealById(String(itemId));
  if (isLoading) return <CardSkeleton />;
  if (!data) return null;
  return (
    <SavedCard
      itemType="deal"
      href={`/deals/${itemId}`}
      title={data.title}
      sub={data.businessName ?? undefined}
      meta={data.category}
      savedId={savedId}
      onUnsave={onUnsave}
    />
  );
}

function BusinessCard({ itemId, savedId, onUnsave }: { itemId: number; savedId: number; onUnsave: (id: number) => void }) {
  const { data, isLoading } = useGetBusinessById(String(itemId));
  if (isLoading) return <CardSkeleton />;
  if (!data) return null;
  return (
    <SavedCard
      itemType="business"
      href={`/businesses/${itemId}`}
      title={data.name}
      sub={data.description ?? undefined}
      meta={[data.category, data.town].filter(Boolean).join(" · ")}
      savedId={savedId}
      onUnsave={onUnsave}
    />
  );
}

function JobCard({ itemId, savedId, onUnsave }: { itemId: number; savedId: number; onUnsave: (id: number) => void }) {
  const { data, isLoading } = useGetJobById(itemId);
  if (isLoading) return <CardSkeleton />;
  if (!data) return null;
  return (
    <SavedCard
      itemType="job"
      href={`/jobs/${itemId}`}
      title={data.title}
      meta={data.town}
      savedId={savedId}
      onUnsave={onUnsave}
    />
  );
}

function SavedCard({ itemType, href, title, sub, meta, savedId, onUnsave }: {
  itemType: ItemType;
  href: string;
  title: string;
  sub?: string;
  meta?: string;
  savedId: number;
  onUnsave: (id: number) => void;
}) {
  const { bg, text } = TYPE_COLORS[itemType];
  return (
    <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 flex gap-3 items-start hover:shadow-md transition-shadow">
      <Link href={href} className="flex-1 min-w-0 group">
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full mb-1.5 ${bg} ${text}`}>
          {TYPE_LABELS[itemType]}
        </span>
        <p className="font-semibold text-slate-800 line-clamp-2 group-hover:text-primary transition-colors">{title}</p>
        {sub && <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">{sub}</p>}
        {meta && <p className="text-xs text-slate-400 mt-1">{meta}</p>}
      </Link>
      <button
        onClick={() => onUnsave(savedId)}
        className="p-1.5 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
        title="Remove from saved"
      >
        <Trash2 size={15} />
      </button>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-4 space-y-2 animate-pulse">
      <div className="h-4 w-14 bg-slate-100 rounded-full" />
      <div className="h-4 w-3/4 bg-slate-100 rounded" />
      <div className="h-3 w-1/2 bg-slate-100 rounded" />
    </div>
  );
}

export default function SavedItems() {
  useSEO({ title: "Saved Items — Ayrshire Hub", description: "Your bookmarked news, events, deals, businesses and jobs on Ayrshire Hub." });
  const { user, isLoading: authLoading } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>("all");

  const queryParams = activeTab !== "all" ? { itemType: activeTab } : {};
  const { data, isLoading, refetch } = useGetSavedItems(queryParams);
  const { mutate: deleteSaved } = useDeleteSavedItem();

  if (authLoading) return null;
  if (!user) return <Redirect to="/login" />;

  const items = data?.items ?? [];

  const handleUnsave = (savedId: number) => {
    if (!confirm("Remove this item from your saved list?")) return;
    deleteSaved({ id: savedId }, { onSuccess: () => { void refetch(); } });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl font-bold text-primary">Saved Items</h1>
          <p className="text-sm text-slate-500 mt-1">Your bookmarked content</p>

          <div className="flex gap-1 mt-6 flex-wrap">
            {TABS.map(({ key, label, icon }) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold border transition-all ${
                  activeTab === key
                    ? "bg-primary text-white border-primary"
                    : "bg-white border-slate-200 text-slate-600 hover:border-primary/40"
                }`}
              >
                {icon}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {isLoading ? (
          <div className="space-y-3 max-w-2xl">
            {[1, 2, 3].map(i => <CardSkeleton key={i} />)}
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 rounded-3xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Bookmark size={32} className="text-primary" />
            </div>
            <p className="text-slate-600 font-semibold text-lg">Nothing saved yet</p>
            <p className="text-slate-400 text-sm mt-1">Tap the bookmark icon on news, events, deals, businesses or jobs to save them here.</p>
            <Link href="/" className="inline-block mt-6 bg-primary hover:bg-primary/90 text-white font-bold px-6 py-3 rounded-xl transition-colors">
              Browse the Hub
            </Link>
          </div>
        ) : (
          <div className="space-y-3 max-w-2xl">
            <p className="text-sm text-slate-500">{items.length} saved item{items.length !== 1 ? "s" : ""}</p>
            {items.map(item => {
              const props = { itemId: item.itemId, savedId: item.id, onUnsave: handleUnsave };
              if (item.itemType === "news")     return <NewsCard key={item.id} {...props} />;
              if (item.itemType === "event")    return <EventCard key={item.id} {...props} />;
              if (item.itemType === "deal")     return <DealCard key={item.id} {...props} />;
              if (item.itemType === "business") return <BusinessCard key={item.id} {...props} />;
              if (item.itemType === "job")      return <JobCard key={item.id} {...props} />;
              return null;
            })}
          </div>
        )}
      </div>
    </div>
  );
}
